import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowLeft, Clock, Calendar, Tag, Users, Smartphone, Volume2, FileSearch, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '../../sections/Navbar';
import Footer from '../../sections/Footer';

gsap.registerPlugin(ScrollTrigger);

const Article2Page = () => {
  const articleRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const ctx = gsap.context(() => {
      gsap.from(contentRef.current?.querySelectorAll('.animate-section') || [], {
        scrollTrigger: {
          trigger: contentRef.current,
          start: 'top 80%',
          toggleActions: 'play none none reverse',
        },
        opacity: 0,
        y: 30,
        duration: 0.6,
        stagger: 0.1,
        ease: 'power2.out',
      });
    }, articleRef);

    return () => ctx.revert();
  }, []);

  return (
    <div ref={articleRef} className="min-h-screen bg-gradient-to-br from-sky-50 via-cyan-50 to-emerald-50">
      <Navbar />
      
      <article ref={contentRef} className="relative z-10 pt-24 pb-16">
        {/* Hero Image */}
        <div className="animate-section relative h-[50vh] min-h-[400px] overflow-hidden">
          <img 
            src="/blog/article-2-hero.jpg" 
            alt="Růst AI vyhledávání" 
            className="w-full h-full object-cover opacity-90"
            style={{ filter: 'hue-rotate(180deg) saturate(0.8)' }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-sky-50 via-transparent to-transparent" />
          
          <div className="absolute bottom-0 left-0 right-0 px-4 sm:px-6 lg:px-8 pb-12">
            <div className="max-w-4xl mx-auto">
              <Link to="/blog">
                <Button
                  variant="ghost"
                  className="mb-4 text-slate-600 hover:text-slate-800 -ml-4"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Zpět na blog
                </Button>
              </Link>
              
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="flex items-center gap-1 px-3 py-1 rounded-full text-sm bg-[#00d4aa]/20 text-[#00d4aa]">
                  <Tag className="w-3 h-3" />
                  Trendy
                </span>
                <span className="flex items-center gap-1 text-sm text-slate-500">
                  <Calendar className="w-4 h-4" />
                  6. února 2026
                </span>
                <span className="flex items-center gap-1 text-sm text-slate-500">
                  <Clock className="w-4 h-4" />
                  6 min čtení
                </span>
              </div>
              
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-slate-800">
                Proč poroste <span className="text-gradient">AI vyhledávání</span>: 6 důvodů a dopad na weby
              </h1>
            </div>
          </div>
        </div>

        {/* Article Content */}
        <div className="px-4 sm:px-6 lg:px-8 py-12">
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-lg max-w-none">
              <div className="animate-section">
                <p className="text-slate-600 leading-relaxed text-xl">
                  AI vyhledávání roste, protože řeší skutečný problém: lidé nechtějí procházet desítky stránek. Chtějí rychlou odpověď, která jim pomůže se rozhodnout.
                </p>
              </div>

              <div className="animate-section my-12">
                <h2 className="text-2xl font-bold mb-6 flex items-center gap-3 text-slate-800">
                  <Users className="w-7 h-7 text-[#0ea5e9]" />
                  1. Lidé chtějí závěr, ne rešerši
                </h2>
                
                <p className="text-slate-600 leading-relaxed">
                  U řady dotazů existuje odpověď, ale je rozptýlená po mnoha stránkách. AI dává shrnutí, vytáhne hlavní rozdíly a nabídne varianty. Funguje to u dotazů jako:
                </p>
                
                <ul className="space-y-3 my-6">
                  {['co je pro mě vhodné a proč', 'srovnání A vs B', 'na co si dát pozor', 'jaký je postup'].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-600">
                      <div className="w-2 h-2 rounded-full bg-[#0ea5e9]" />
                      {item}
                    </li>
                  ))}
                </ul>
                
                <p className="text-slate-600 leading-relaxed">
                  To jsou dotazy, které často předcházejí nákupu.
                </p>
              </div>

              <div className="animate-section my-12">
                <h2 className="text-2xl font-bold mb-6 text-slate-800">2. Složitost výběru roste</h2>
                
                <p className="text-slate-600 leading-relaxed">
                  V e-commerce přibývá parametrů, variant, recenzí. U služeb přibývá možností a balíčků. AI snižuje zátěž: udělá pořádek v tom, co je důležité, a pomůže rychleji se rozhodnout.
                </p>
              </div>

              <div className="animate-section my-12">
                <h2 className="text-2xl font-bold mb-6 text-slate-800">3. Vyhledávání se mění na dialog</h2>
                
                <p className="text-slate-600 leading-relaxed">
                  Klasické vyhledávání končí článkem, který musíte přečíst. AI vyhledávání dává výsledek: doporučí variantu, položí doplňující otázku, navrhne další krok. Je to přirozenější.
                </p>
                
                <div className="glass-card rounded-xl p-6 my-8 border-l-4 border-[#00d4aa]">
                  <p className="text-slate-600 m-0">
                    Jak se to promítá do SEO:{' '}
                    <Link to="/blog/ai-meni-vysledky-vyhledavani" className="text-[#0ea5e9] hover:underline">
                      AI mění výsledky vyhledávání
                    </Link>
                  </p>
                </div>
              </div>

              <div className="animate-section my-12">
                <h2 className="text-2xl font-bold mb-6 text-slate-800">4. Důvěryhodnost je klíčová</h2>
                
                <p className="text-slate-600 leading-relaxed">
                  Uživatelé mají malou toleranci k prázdným frázím. Když AI shrnuje informace, musí být:
                </p>
                
                <ul className="space-y-3 my-6">
                  {['jednoznačné', 'konzistentní napříč webem', 've struktuře, kterou AI použije'].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-600">
                      <div className="w-2 h-2 rounded-full bg-[#14b8a6]" />
                      {item}
                    </li>
                  ))}
                </ul>
                
                <p className="text-slate-600 leading-relaxed">
                  Proto je důležité řešit AI SEO — použitelnost jako zdroj.
                </p>
              </div>

              <div className="animate-section my-12">
                <h2 className="text-2xl font-bold mb-6 flex items-center gap-3 text-slate-800">
                  <Smartphone className="w-7 h-7 text-[#14b8a6]" />
                  <Volume2 className="w-7 h-7 text-[#14b8a6]" />
                  5. Mobil a hlas posilují jednu odpověď
                </h2>
                
                <p className="text-slate-600 leading-relaxed">
                  Na mobilu se špatně porovnává a čte. U hlasových dotazů je přirozené čekat jednu odpověď. AI vyhledávání na to sedí lépe než seznam odkazů.
                </p>
              </div>

              <div className="animate-section my-12">
                <h2 className="text-2xl font-bold mb-6 flex items-center gap-3 text-slate-800">
                  <FileSearch className="w-7 h-7 text-[#22c55e]" />
                  Co to znamená pro váš web
                </h2>
                
                <p className="text-slate-600 leading-relaxed">
                  Základní pravidlo je jednoduché: odpověď musí být rychle dostupná a dobře strukturovaná.
                </p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-8">
                  <Link
                    to="/blog/ai-meni-vysledky-vyhledavani"
                    className="glass-card rounded-lg p-5 text-left hover:border-[#0ea5e9]/50 transition-colors group"
                  >
                    <div className="text-sm text-[#0ea5e9] mb-2">Průvodce</div>
                    <div className="font-semibold text-slate-800 group-hover:text-gradient transition-colors">Jak psát citovatelný obsah</div>
                  </Link>
                  <Link
                    to="/blog/ai-seo-bez-chaosu-prvni-mesic"
                    className="glass-card rounded-lg p-5 text-left hover:border-[#14b8a6]/50 transition-colors group"
                  >
                    <div className="text-sm text-[#14b8a6] mb-2">Plán</div>
                    <div className="font-semibold text-slate-800 group-hover:text-gradient transition-colors">Postup bez chaosu</div>
                  </Link>
                </div>
              </div>

              {/* Related Articles */}
              <div className="animate-section my-12">
                <h3 className="text-xl font-bold mb-6 text-slate-800">Další čtení</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Link
                    to="/blog/ai-meni-vysledky-vyhledavani"
                    className="glass-card rounded-xl p-5 text-left hover:border-[#0ea5e9]/50 transition-colors group"
                  >
                    <div className="text-sm text-[#0ea5e9] mb-2">AI SEO</div>
                    <div className="font-semibold text-slate-800 group-hover:text-gradient transition-colors">Jak AI mění výsledky vyhledávání</div>
                  </Link>
                  <Link
                    to="/blog/ai-seo-bez-chaosu-prvni-mesic"
                    className="glass-card rounded-xl p-5 text-left hover:border-[#14b8a6]/50 transition-colors group"
                  >
                    <div className="text-sm text-[#14b8a6] mb-2">Průvodce</div>
                    <div className="font-semibold text-slate-800 group-hover:text-gradient transition-colors">AI SEO bez chaosu: 7 priorit</div>
                  </Link>
                </div>
              </div>

              {/* CTA */}
              <div className="animate-section my-12">
                <div className="glass-card rounded-2xl p-8 text-center border border-[#00d4aa]/30">
                  <h3 className="text-2xl font-bold mb-4 text-slate-800">Připraveni na budoucnost vyhledávání?</h3>
                  <p className="text-slate-500 mb-6">
                    Zjistěte, jak je váš web připraven na růst AI vyhledávání.
                  </p>
                  <Link to="/audit">
                    <Button
                      size="lg"
                      className="bg-gradient-to-r from-[#0ea5e9] to-[#00d4aa] text-white font-semibold px-8"
                    >
                      AI Visibility Audit
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>
      
      <Footer />
    </div>
  );
};

export default Article2Page;
